import { useCallback, useContext, useMemo } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Flex, IconCommunity, IconWarning, PropertyCard, Tag } from '@zillow/constellation';
import { Text } from '@zillow/constellation-10';

import { AccountViewListingStatus } from '@zg-rentals/rental-manager-env';

import { RMWContext } from '../../../../providers/RMWContext';

import PropertiesActions from '../../../../actions/PropertiesActions';

import { useSelector } from '../../../../selectors/useSelector';

import { getDashboardOrSOHPath } from '../../../../utils/pathUtils';

import propertyUtils from '../../../../utils/propertyUtils';
import { getTagTypeByKey, STATUS_KEY } from '../../../../utils/statusTagUtils';

import PropertiesPageCSEvents from '../../../../model/ClickstreamEvent/PropertiesPageCSEvents';

import { StyledFallbackPhoto, StyledPropertyCard } from './StyledComponents';

import type { MouseEvent } from 'react';
import type { PhotoUrlsProps, PropertyProps } from '../../../../model/Property';

type Props = {
  listing: PropertyProps;
};

const PropertyCardSimple = ({
  listing: { isMultifamily, address, photoUrls, status, syndicationOverviewStatus, listingAlias },
}: Props): JSX.Element => {
  const { paths } = useContext(RMWContext);
  const dispatch = useDispatch();

  const { canAccessDashboardRedesign, user, isEnterpriseCustomer } = useSelector(
    (state) => ({
      canAccessDashboardRedesign: state.app.canAccessDashboardRedesign,
      user: state.user,
      isEnterpriseCustomer: state.user.isEnterpriseCustomer,
    }),
    shallowEqual,
  );

  const { formattedCityStateZip, formattedCompleteAddress, formattedName, formattedStreet } = useMemo(() => {
    return propertyUtils.formatAddress(address);
  }, [address]);

  // NOTE: `handleOnClick` also handles `ENTER` and `SPACE` keyUp
  const handleOnClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      PropertiesPageCSEvents.PropertyCardClick({ user });
      dispatch(PropertiesActions.updateCurrentProperty({ ...address }));

      /**
       * since isMultifamily is a prop passed in
       * the canAccessDashboardRedesign is out of sync until a few rerenders later
       * we need to pass isMultifamily to getDashboardOrSOHPath
       **/
      const pathToRoute = getDashboardOrSOHPath({
        canAccessDashboardRedesign,
        isMultifamily,
        listingAlias,
        propertiesPage: paths?.propertiesPage,
      });

      if (e.metaKey || e.ctrlKey) {
        window.router.updateLocation(pathToRoute, '_blank');

        return;
      }

      window.router.transitionTo(pathToRoute);
    },
    [listingAlias],
  );

  const getTagIcon = () => {
    if (status.status === AccountViewListingStatus.UNABLE_TO_PUBLISH) {
      return <IconWarning />;
    }
    if (isMultifamily) {
      return <IconCommunity />;
    }
    return null;
  };

  return (
    <Flex data-testid="PropertyCardSimple" flex="1 1 30%">
      <StyledPropertyCard
        aria-label={formattedCompleteAddress}
        data-eventid={PropertiesPageCSEvents.PropertyCardClick.data_event_id}
        data={{
          title: (
            <Text textStyle="heading-sm" asChild css={{ fontFamily: 'heading-alt' }}>
              <h4>{formattedName ? formattedName : formattedStreet}</h4>
            </Text>
          ),
          body1: formattedCityStateZip,
          body2: (
            <Tag
              appearance={
                isEnterpriseCustomer
                  ? getTagTypeByKey(STATUS_KEY.SYNDICATION_OVERVIEW_STATUS, syndicationOverviewStatus)
                  : status.tagAppearance || getTagTypeByKey(STATUS_KEY.LISTING_DISPLAY_STATUS, status.status)
              }
              icon={getTagIcon()}
              marginRight="xs"
            >
              {isEnterpriseCustomer ? status.message : status.text}
            </Tag>
          ),
        }}
        photoBody={
          (photoUrls as PhotoUrlsProps)?.medium ? (
            <PropertyCard.Photo src={photoUrls.medium} alt={formattedCompleteAddress} />
          ) : (
            <StyledFallbackPhoto role="img" alt={formattedCompleteAddress} />
          )
        }
        onClick={handleOnClick}
      />
    </Flex>
  );
};

export default PropertyCardSimple;
